import { forwardRef, useId } from 'react';

import { cn } from '@/shared/lib/cn';

const FIELD_BASE =
  'w-full rounded border border-border bg-bg-subtle px-3 text-base text-fg placeholder:text-fg-subtle ' +
  'transition-colors duration-150 hover:border-border-strong ' +
  'focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/40 ' +
  'disabled:cursor-not-allowed disabled:opacity-60 ' +
  'aria-[invalid=true]:border-danger aria-[invalid=true]:ring-danger/30';

export interface FieldProps {
  label?: string;
  hint?: string;
  error?: string | null;
  required?: boolean;
  /** Renders as an element after the label row, e.g. a unit or a toggle. */
  action?: React.ReactNode;
}

/** The label/hint/error wrapper every form control shares. */
export function Field({
  label,
  hint,
  error,
  required,
  action,
  htmlFor,
  children,
  className,
}: FieldProps & { htmlFor?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('space-y-1.5', className)}>
      {(label || action) && (
        <div className="flex items-center justify-between gap-2">
          {label && (
            <span className="flex items-baseline text-sm font-medium text-fg">
              <label htmlFor={htmlFor}>{label}</label>
              {/* The marker sits outside <label> so the field's accessible name — and the
                  label text assistive tech reads — is exactly the label, not "Label*".
                  The control itself carries aria-required. */}
              {required && (
                <span className="ml-0.5 text-danger" aria-hidden>
                  *
                </span>
              )}
            </span>
          )}
          {action}
        </div>
      )}
      {children}
      {error ? (
        <p className="text-xs text-danger" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="text-xs text-fg-subtle">{hint}</p>
      ) : null}
    </div>
  );
}

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    FieldProps {
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
  wrapperClassName?: string;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, hint, error, required, action, leading, trailing, className, wrapperClassName, id, ...props },
  ref,
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      action={action}
      htmlFor={inputId}
      className={wrapperClassName}
    >
      <div className="relative flex items-center">
        {leading && (
          <span className="pointer-events-none absolute left-3 text-fg-subtle" aria-hidden>
            {leading}
          </span>
        )}
        <input
          ref={ref}
          id={inputId}
          required={required}
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          className={cn(FIELD_BASE, 'h-9', leading && 'pl-9', trailing && 'pr-9', className)}
          {...props}
        />
        {trailing && <span className="absolute right-2 flex items-center">{trailing}</span>}
      </div>
    </Field>
  );
});

export interface TextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    FieldProps {
  wrapperClassName?: string;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, hint, error, required, action, className, wrapperClassName, id, rows = 4, ...props },
  ref,
) {
  const generatedId = useId();
  const textareaId = id ?? generatedId;
  return (
    <Field
      label={label}
      hint={hint}
      error={error}
      required={required}
      action={action}
      htmlFor={textareaId}
      className={wrapperClassName}
    >
      <textarea
        ref={ref}
        id={textareaId}
        rows={rows}
        required={required}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        className={cn(FIELD_BASE, 'resize-y py-2 leading-relaxed', className)}
        {...props}
      />
    </Field>
  );
});
